import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { CRMLayout } from "@/components/CRMLayout";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Play, Plus, Trash2, Save, Rocket, CheckCircle2, XCircle, Terminal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ProjectTabs from "../components/ProjectTabs";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function ProjectServerPage() {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const [commands, setCommands] = useState([]);
  const [folder, setFolder] = useState("");
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [running, setRunning] = useState(null);
  const [log, setLog] = useState([]);
  const [result, setResult] = useState(null);
  const logRef = useRef(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/server/commands`, auth)
      .then(({ data }) => { setCommands(data.commands || []); setFolder(data.folder_path || ""); })
      .catch(() => toast.error("Impossible de charger les commandes"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, [log]);

  const saveCommands = async () => {
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/server/commands`, { commands }, auth);
      toast.success("Commandes enregistrées"); setEdit(false);
    } catch (e) { toast.error("Erreur d'enregistrement"); }
  };

  const runCommand = async (index) => {
    if (running !== null) return;
    setRunning(index); setLog([]); setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}/server/run/${index}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          let evt; try { evt = JSON.parse(p.slice(6)); } catch { continue; }
          if (evt.type === "line") setLog((l) => [...l, evt.text]);
          else if (evt.type === "error") { setLog((l) => [...l, "⚠ " + evt.message]); setResult({ ok: false }); }
          else if (evt.type === "done") setResult({ ok: evt.ok, code: evt.code });
        }
      }
    } catch (e) {
      toast.error("Erreur d'exécution"); setResult({ ok: false });
    } finally {
      setRunning(null);
    }
  };

  const Tabs = () => (
    <ProjectTabs projectId={projectId} actif="serveur" />
  );

  if (loading) {
    return <CRMLayout><div className="flex items-center justify-center py-32 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div></CRMLayout>;
  }

  return (
    <CRMLayout>
      <div className="p-6 lg:p-8 max-w-3xl">
        <Link to="/dashboard/projects" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Retour aux projets
        </Link>
        <Tabs />

        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-bold text-slate-900">Commandes serveur</h2>
            <p className="text-xs text-slate-500">Exécutées dans <code className="bg-slate-100 px-1 rounded">{folder || "—"}</code></p>
          </div>
          {user?.role === "admin" && (
            <Button size="sm" variant="outline" onClick={() => setEdit((v) => !v)}>{edit ? "Terminer" : "Configurer"}</Button>
          )}
        </div>

        {/* Boutons d'action */}
        {!edit ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
            {commands.length === 0 && <div className="text-sm text-slate-400 col-span-2 py-4">Aucune commande de déploiement n'est configurée pour ce projet.</div>}
            {commands.map((c, i) => (
              <button key={i} onClick={() => runCommand(i)} disabled={running !== null}
                className="flex items-center gap-2.5 border border-slate-200 rounded-lg px-4 py-3 text-left hover:border-indigo-400 hover:bg-indigo-50/30 disabled:opacity-50">
                {running === i ? <Loader2 className="w-4 h-4 animate-spin text-indigo-600" /> : (i === 0 ? <Rocket className="w-4 h-4 text-indigo-600" /> : <Play className="w-4 h-4 text-slate-500" />)}
                <span className="text-sm font-semibold text-slate-800">{c.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 space-y-2">
            {commands.map((c, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input value={c.label} onChange={(e) => setCommands((cs) => cs.map((x, k) => k === i ? { ...x, label: e.target.value } : x))} placeholder="Libellé" className="w-48 h-9" />
                <Input value={c.command} onChange={(e) => setCommands((cs) => cs.map((x, k) => k === i ? { ...x, command: e.target.value } : x))} placeholder="commande shell" className="flex-1 h-9 font-mono text-xs" />
                <button onClick={() => setCommands((cs) => cs.filter((_, k) => k !== i))} className="text-red-500 hover:bg-red-50 rounded p-2"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" onClick={() => setCommands((cs) => [...cs, { label: "", command: "" }])}><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
              <Button size="sm" onClick={saveCommands} className="bg-indigo-600 hover:bg-indigo-700"><Save className="w-4 h-4 mr-1" /> Enregistrer</Button>
            </div>
            <p className="text-[11px] text-amber-600 pt-1">⚠️ Ces commandes s'exécutent sur le serveur en tant que root, dans le dossier du projet. N'y mettez que des commandes de confiance (build, deploy, restart).</p>
          </div>
        )}

        {/* Console de sortie */}
        {(running !== null || log.length > 0 || result) && (
          <div className="bg-slate-900 rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
              <span className="text-xs font-semibold text-slate-300 inline-flex items-center gap-1.5"><Terminal className="w-3.5 h-3.5" /> Sortie</span>
              {result && (result.ok
                ? <span className="text-xs font-semibold text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Terminé</span>
                : <span className="text-xs font-semibold text-red-400 inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Échec {result.code != null ? `(code ${result.code})` : ""}</span>)}
              {running !== null && <span className="text-xs text-indigo-300 inline-flex items-center gap-1"><Loader2 className="w-3.5 h-3.5 animate-spin" /> En cours…</span>}
            </div>
            <pre ref={logRef} className="text-xs font-mono text-slate-200 p-4 max-h-96 overflow-auto leading-relaxed">
              {log.length === 0 ? <span className="text-slate-500">Démarrage…</span> : log.join("\n")}
            </pre>
          </div>
        )}
      </div>
    </CRMLayout>
  );
}
